import { createContext, useContext, useCallback, useRef, useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Btn, Field } from "@/components/ui-kit";
import { Warning, X } from "@phosphor-icons/react";

const DeleteConfirmContext = createContext(null);

/**
 * Imperative delete confirmation.
 *   const confirmDelete = useDeleteConfirm();
 *   const reason = await confirmDelete({ message: "Delete this job?" });
 *   if (reason === null) return;              // user cancelled
 *   await api.delete(`/jobs/${id}?reason=${encodeURIComponent(reason)}`);
 *
 * Super admins get a plain confirm (reason = ""). Everyone else must type a
 * reason/remark that is carried into the super-admin approval queue.
 */
export function DeleteConfirmProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(null); // { message, title, requireReason }
  const [reason, setReason] = useState("");
  const [err, setErr] = useState("");
  const resolver = useRef(null);

  const confirmDelete = useCallback(
    ({ message, title = "Confirm deletion" } = {}) => {
      const requireReason = user?.role !== "super_admin";
      // Resolve any previously-pending request as cancelled to avoid leaking its promise.
      if (resolver.current) { resolver.current(null); resolver.current = null; }
      setReason("");
      setErr("");
      setState({ message, title, requireReason });
      return new Promise((resolve) => { resolver.current = resolve; });
    },
    [user]
  );

  const close = (value) => {
    const resolve = resolver.current;
    resolver.current = null;
    setState(null);
    resolve && resolve(value);
  };

  // Dismiss on Escape for consistency with other blocking modals.
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === "Escape") close(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const onConfirm = () => {
    if (state?.requireReason && !reason.trim()) {
      setErr("A reason/remark is required to send this for approval.");
      return;
    }
    close(state?.requireReason ? reason.trim() : "");
  };

  return (
    <DeleteConfirmContext.Provider value={confirmDelete}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" data-testid="delete-confirm-modal"
          onMouseDown={(e) => { if (e.target === e.currentTarget) close(null); }}>
          <div className="card-flat w-full max-w-md p-6">
            <div className="flex items-start justify-between mb-3">
              <h3 className="heading text-lg font-bold flex items-center gap-2 text-red-700">
                <Warning size={20} weight="fill" /> {state.title}
              </h3>
              <button type="button" onClick={() => close(null)} data-testid="delete-confirm-close"
                className="text-[var(--text-3)] hover:text-[var(--text-1)]"><X size={20} /></button>
            </div>
            <p className="text-sm text-[var(--text-2)] mb-4" data-testid="delete-confirm-message">{state.message}</p>

            {state.requireReason && (
              <Field label="Reason / remark *">
                <textarea
                  data-testid="delete-confirm-reason"
                  autoFocus
                  rows={3}
                  value={reason}
                  onChange={(e) => { setReason(e.target.value); if (err) setErr(""); }}
                  placeholder="Why is this being deleted? This is sent to a Super Admin for approval."
                  className="w-full rounded-sm border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] resize-none"
                />
              </Field>
            )}
            {err && <div className="text-xs text-red-600 mt-1" data-testid="delete-confirm-error">{err}</div>}

            <div className="flex gap-2 items-center mt-5">
              <Btn type="button" variant="danger" data-testid="delete-confirm-submit" onClick={onConfirm}>
                {state.requireReason ? "Request deletion" : "Delete"}
              </Btn>
              <Btn variant="ghost" type="button" data-testid="delete-confirm-cancel" onClick={() => close(null)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </DeleteConfirmContext.Provider>
  );
}

export const useDeleteConfirm = () => useContext(DeleteConfirmContext);
